import { bundleService } from '../../api/services/bundleService';
import { decodeJsonMaybeGzip } from './gunzip';
import {
  readBundleRecord,
  writeBundleRecord,
  getMemoryBundleRecord,
} from './bundleStorage';
import {
  extractPermissionsFromBundle,
  hasPermissionEntries,
} from './permissionsUtils';

let pendingGetBundlePromise = null;

function useCachedBundle(cached, globalCacheManager, reason) {
  if (cached?.bundle) {
    console.warn(`[getBundle] ${reason} — using local cached bundle (version=${cached.version})`);
    globalCacheManager.distributeFromBundle(cached.bundle);
    return cached.bundle;
  }
  return null;
}

/**
 * Bundle 메타 확인 → version 같으면 GCS 스킵 → 캐시 분배
 * GCS 다운로드 실패(대개 CORS) 시 IndexedDB/메모리 캐시로 폴백
 */
export async function getBundle({ forceRefresh = false, globalCacheManager } = {}) {
  if (!globalCacheManager) {
    throw new Error('getBundle requires globalCacheManager');
  }

  if (pendingGetBundlePromise) {
    return pendingGetBundlePromise;
  }

  pendingGetBundlePromise = (async () => {
    const cached = (await readBundleRecord()) || getMemoryBundleRecord();

    const metaResult = await bundleService.getLatestMeta();
    if (!metaResult.success) {
      const fallback = useCachedBundle(
        cached,
        globalCacheManager,
        `bundle meta failed: ${metaResult.error?.message || 'unknown'}`
      );
      if (fallback) return fallback;

      const err = new Error(
        metaResult.error?.message || 'Failed to fetch bundle meta'
      );
      err.status = metaResult.error?.status || metaResult.error?.code;
      err.bundleMetaFailed = true;
      err.originalError = metaResult.error;
      throw err;
    }

    const meta = metaResult.data;

    if (!forceRefresh && cached?.version === meta.version && cached.bundle) {
      const cachedPerms = extractPermissionsFromBundle(cached.bundle);
      if (hasPermissionEntries(cachedPerms)) {
        globalCacheManager.distributeFromBundle(cached.bundle);
        return cached.bundle;
      }
      console.warn(
        '[getBundle] cached bundle has no usable permissions — re-downloading from GCS'
      );
    }

    if (!meta.downloadUrl) {
      const fallback = useCachedBundle(cached, globalCacheManager, 'meta has no downloadUrl');
      if (fallback) return fallback;
      throw new Error('bundle meta missing downloadUrl');
    }

    let res;
    try {
      res = await fetch(meta.downloadUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch (networkError) {
      const fallback = useCachedBundle(
        cached,
        globalCacheManager,
        `GCS download network error (${networkError?.message}). ` +
          'Often CORS: allow this app origin on the GCS bucket for GET'
      );
      if (fallback) return fallback;

      const err = new Error(
        `Bundle GCS download failed (Failed to fetch). ` +
          `Check GCS bucket CORS allows this origin for GET. URL host: ${safeHost(meta.downloadUrl)}`
      );
      err.cause = networkError;
      err.bundleGcsFailed = true;
      err.isCorsLikely = true;
      throw err;
    }

    if (!res.ok) {
      const fallback = useCachedBundle(
        cached,
        globalCacheManager,
        `GCS HTTP ${res.status}`
      );
      if (fallback) return fallback;
      throw new Error(`bundle download failed: ${res.status}`);
    }

    let bundle;
    try {
      bundle = await decodeJsonMaybeGzip(res, meta.contentEncoding);
    } catch (decodeError) {
      const fallback = useCachedBundle(
        cached,
        globalCacheManager,
        `bundle decode failed: ${decodeError?.message}`
      );
      if (fallback) return fallback;
      throw decodeError;
    }

    const permissionCategories = Object.keys(extractPermissionsFromBundle(bundle));
    console.info('[getBundle] downloaded bundle keys:', Object.keys(bundle || {}), {
      version: bundle?.version,
      department: bundle?.department,
      hasPermissions: permissionCategories.length > 0,
      permissionCategories,
      hasInfo: Boolean(bundle?.info),
      hasMes: Boolean(bundle?.mes),
    });

    const record = {
      bundle,
      version: meta.version,
      etag: meta.etag,
      department: meta.department,
      fetchedAt: Date.now(),
    };

    await writeBundleRecord(record);
    globalCacheManager.distributeFromBundle(bundle);
    return bundle;
  })();

  try {
    return await pendingGetBundlePromise;
  } finally {
    pendingGetBundlePromise = null;
  }
}

function safeHost(url) {
  try {
    return new URL(url).host;
  } catch {
    return '(invalid url)';
  }
}
